import { QuadStarIcon, Typography } from '@ag1/ui';
import { CartLine } from '@portal/api/shopifyV2/types';
import { SHOPIFY_OMEGA_SUB_BUNDLE_VARIANT_ID } from '@portal/config/base';
import { useMoney } from '@portal/shared/hooks/useMoney';
import { getShopifyVariantId } from '@portal/shared/utils/subscriptionV2Helpers';
import { useStoreValue } from '@portal/state/hooks/useStore';
import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  OMEGA_SUBSCRIBE_AND_SAVE_BUNDLE_PRICE,
  OMEGA_SUBSCRIBE_AND_SAVE_PRICE
} from '../../constants';
import { NO_CADENCE_KEY } from '../../util';
import { SiteCartItem } from '../SiteCartItem/SiteCartItem';
import styles from './SiteCartSection.module.scss';

interface Cadence {
  id: string;
  name: string;
  items: CartLine[];
}

type SiteCartSectionProps = {
  sectionName: string;
  items: CartLine[];
  isSubscription?: boolean;
};

export const SiteCartSection = ({
  sectionName = '',
  items = [],
  isSubscription = false
}: SiteCartSectionProps) => {
  const store = useStoreValue();
  const { formatMoney } = useMoney();
  const { t } = useTranslation('siteCart');

  // Detect Omega products and calculate savings
  const omegaSavings = useMemo(() => {
    if (!isSubscription) return null;

    const omegaItems = items.filter((item) => {
      const variantId = getShopifyVariantId(item.merchandise.id);
      const omegaVariants = SHOPIFY_OMEGA_SUB_BUNDLE_VARIANT_ID[store] || {};
      const allOmegaVariantIds = Object.values(omegaVariants).filter(Boolean);
      return allOmegaVariantIds.includes(variantId);
    });

    if (omegaItems.length === 0) return null;

    // Calculate total savings for omega items using predefined savings amounts
    let hasBundlePricing = false;
    const totalSavings = omegaItems.reduce((total, item) => {
      const variantId = getShopifyVariantId(item.merchandise.id);
      const omegaVariants = SHOPIFY_OMEGA_SUB_BUNDLE_VARIANT_ID[store] || {};

      // Check if this is the bundle variant (bundlePrice) vs regular subscription (subscriptionPrice)
      const isBundleVariant = variantId === omegaVariants.bundlePrice;
      if (isBundleVariant) {
        hasBundlePricing = true;
      }

      const savings = isBundleVariant
        ? OMEGA_SUBSCRIBE_AND_SAVE_BUNDLE_PRICE
        : OMEGA_SUBSCRIBE_AND_SAVE_PRICE;
      const itemSavings = savings * item.quantity;
      return total + itemSavings;
    }, 0);

    // Only show bundle savings message if there's actually bundle pricing
    if (totalSavings <= 0 || !hasBundlePricing) return null;

    // Get currency from the first omega item
    const currency = omegaItems[0]?.cost?.totalAmount?.currencyCode || 'USD';

    return {
      amount: totalSavings,
      text: t('omegaBundleSavings', {
        amount: formatMoney(totalSavings, currency, { hideDecimalsForWholeNumbers: true })
      })
    };
  }, [items, isSubscription, store, formatMoney, t]);

  const itemsByCadence = useMemo(() => {
    const noCadenceGroup: Cadence = {
      id: NO_CADENCE_KEY,
      name: '',
      items: [] as CartLine[]
    };

    const groupedItems = items.reduce(
      (acc, item) => {
        const cadence = item.sellingPlanAllocation?.sellingPlan;
        if (!cadence) return acc;

        // Ignore bundle items
        if (item.attributes?.find((attr) => attr.key === '__isBundleItem')?.value === 'true') {
          noCadenceGroup.items.push(item);
          return acc;
        }

        const { id, name } = cadence;

        if (!acc[id]) {
          acc[id] = { id, name, items: [] };
        }
        acc[id].items.push(item);

        return acc;
      },
      {} as Record<string, Cadence>
    );
    const sortedCadenceGroups = Object.values(groupedItems).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
    // Keep no cadence items at the top
    return [noCadenceGroup, ...sortedCadenceGroups];
  }, [items]);

  if (!items?.length) return null;

  return (
    <div className={styles.container}>
      <div className={styles.titleContainer}>
        <Typography variant="captionMono" size="1" className={styles.sectionTitle}>
          {sectionName}
        </Typography>
      </div>
      {/* OTP / Free */}
      {!isSubscription &&
        items.map((item) => (
          <SiteCartItem key={item.id} item={item} isSubscription={isSubscription} />
        ))}
      {/* Sub By Cadence */}
      {isSubscription &&
        itemsByCadence.map((cadence) => (
          <Fragment key={cadence.id}>
            {cadence.id !== NO_CADENCE_KEY && (
              <div className={styles.cadenceSelector}>
                <Typography variant="portalCaption" size="1" noPadding color="brand-green-new">
                  {cadence.name}
                </Typography>
              </div>
            )}
            {cadence.items.map((item) => (
              <SiteCartItem
                key={`${item.id}-${item.merchandise.id}-${item.sellingPlanAllocation?.sellingPlan.id}`}
                item={item}
                isSubscription={isSubscription}
              />
            ))}
          </Fragment>
        ))}
      {/* Bundle Savings */}
      {isSubscription && omegaSavings && (
        <div className={styles.savingsContainer}>
          <QuadStarIcon className={styles.savingsIcon} />
          <Typography variant="portalCaption" size="1" noPadding className={styles.savingsText}>
            {omegaSavings.text}
          </Typography>
        </div>
      )}
    </div>
  );
};
